/**
 * pt-BR is the source of truth for this area's key set, mirroring
 * `src/i18n/pt-BR.ts`'s own convention: this file decides which keys exist,
 * and `en.ts` must supply an English string for every one of them
 * (`__tests__/catalogue.test.ts`).
 *
 * This is the tenant self-serve area's OWN catalogue, read through
 * `defineCatalogue` in `../../../i18n` — never `src/i18n/pt-BR.ts`. See
 * `defineCatalogue`'s module doc for why: three feature areas appending keys
 * to one shared file is the exact collision the route glob was built to
 * avoid, a problem solved for routes but not for messages.
 *
 * Every key here carries a `.source` sibling holding its English text
 * verbatim, the same convention as `estimator.statement.N.source`: a
 * reviewer checking a translation compares it against the pinned English
 * beside it, not against memory. `__tests__/catalogue.test.ts` enforces the
 * pairing over the whole catalogue, not just a few statements, because this
 * catalogue carries vocabulary a paraphrase could quietly break.
 */
export const messages = {
  // -- Balance --------------------------------------------------------------
  // §18.3: a zero balance is the state every tenant begins in and most sit in
  // for a while. `__tests__/catalogue.test.ts` asserts none of these keys use
  // the words for "blocked", "locked", "denied" — a zero balance is not a
  // fault, and the vocabulary says so.
  'balance.total': 'Total',
  'balance.total.source': 'Total',
  'balance.figure': 'Saldo',
  'balance.figure.source': 'Balance',
  'balance.available': 'Disponível',
  'balance.available.source': 'Available',
  'balance.held': 'Reservado',
  'balance.held.source': 'Set aside',

  // -- Users page -------------------------------------------------------
  // "Usuários", not "Equipe": a campaign has its own team structure.
  'team.heading': 'Usuários',
  'team.heading.source': 'Users',

  // -- Members ------------------------------------------------------------
  'members.heading': 'Pessoas',
  'members.heading.source': 'People',
  // A member with a null campaign_id holds a tenant-wide grant, not "no
  // campaign" -- §5.1 draws the distinction and the list must not blur it.
  'members.scope.tenantWide': 'Toda a organização',
  'members.scope.tenantWide.source': 'Organisation-wide',
  // Removal takes effect on one click with no undo, the same as revoking an
  // invitation (`invitations.revoke.confirm`) -- both go through
  // `window.confirm` rather than one of them silently skipping it.
  'members.remove.confirm': 'Remover {email} da organização? A pessoa perde o acesso na hora.',
  'members.remove.confirm.source':
    'Remove {email} from the organisation? They lose access immediately.',
  // A member whose address is not verified yet (`/control/me` stores NULL).
  // The row still renders -- never filtered out for lacking one.
  'members.noEmail': 'sem e-mail',
  'members.noEmail.source': 'no email',

  // -- Role picker -----------------------------------------
  // Distinct from `members.role.*` above: those name a role in the
  // member list; these describe what each role DOES, read inside the
  // dropdown that assigns one -- a reader picking a role wants the
  // difference between them explained, not the same three names repeated.
  'role.TENANT_ADMIN': 'Administra a organização',
  'role.TENANT_ADMIN.source': 'Administers the organisation',
  'role.CAMPAIGN_ANALYST': 'Trabalha nas campanhas a que tem acesso',
  'role.CAMPAIGN_ANALYST.source': 'Works on the campaigns they have access to',
  'role.ADDRESS_READER': 'Vê os endereços cadastrados',
  'role.ADDRESS_READER.source': 'Sees the addresses on file',
  // The picker's own trigger, closed: a short title, not the sentence above
  // -- the trigger has no width to spare for a full description, and the
  // open list still shows the sentence for the same role.
  'role.short.TENANT_ADMIN': 'Admin da organização',
  'role.short.TENANT_ADMIN.source': 'Org admin',
  'role.short.CAMPAIGN_ANALYST': 'Analista',
  'role.short.CAMPAIGN_ANALYST.source': 'Analyst',
  'role.short.ADDRESS_READER': 'Leitura de endereços',
  'role.short.ADDRESS_READER.source': 'Address reader',
  // §5.1: the address role is granted, never invited. The invitation
  // picker renders this sentence in place of a selectable row, rather than
  // omitting the role and leaving an unexplained gap where a fourth option
  // would otherwise be.
  'role.address.not_invitable':
    'Esse acesso não vai por convite. Você libera depois, aqui na lista de pessoas.',
  'role.address.not_invitable.source':
    'This access does not go by invitation. You grant it afterwards, here in the people list.',
  // §4.7/§16: the confirmation names exactly what the role does and nothing
  // more, so the grant reads as a scoped, deliberate act rather than a blank
  // "are you sure?".
  'members.grant_address.confirm':
    'Liberar os endereços para {email}? Essa pessoa passa a ver os endereços cadastrados, e ' +
    'nada além disso.',
  'members.grant_address.confirm.source':
    'Grant {email} access to the addresses? They will see the addresses on file, and nothing ' +
    'else.',
  // The rule lives in core: a tenant keeps at least one
  // tenant-wide administrator. The client does not count admins or predict
  // this outcome -- it shows this sentence only once the API has actually
  // refused a role change with a 409, the one conflict this route defines.
  'members.last_admin':
    'Esta pessoa é a última administradora. Promova outra antes de mudar este acesso.',
  'members.last_admin.source':
    'This person is the last administrator. Promote somebody else before changing this access.',

  // -- Invitations (§18.2) ---------------------------------------------------
  // The list renders only open invitations, with no closed-invitations
  // toggle, so "sent" is accurate rather than a claim about the whole
  // history.
  'invitations.heading': 'Convites enviados',
  'invitations.heading.source': 'Invitations sent',
  'invitations.create.emailLabel': 'E-mail da pessoa convidada',
  'invitations.create.emailLabel.source': "Invited person's email",
  'invitations.create.roleLabel': 'Acesso',
  'invitations.create.roleLabel.source': 'Access',
  'invitations.create.roleHint': 'Vale a partir do momento em que a pessoa aceitar.',
  'invitations.create.roleHint.source': 'It takes effect the moment they accept.',
  'invitations.revoke.confirm': 'Cancelar este convite? O link deixa de funcionar na hora.',
  'invitations.revoke.confirm.source':
    'Cancel this invitation? The link stops working immediately.',

  // -- Credit requests (§18.6) ---------------------------------------------------
  'creditRequests.create.heading': 'Pedir mais créditos',
  'creditRequests.create.heading.source': 'Ask for more credits',

  // -- Balance, the acquisition status line ---------------
  // The compact status line `AcquisitionStatus` renders on every screen that
  // needs to say whether following is running.
  'balance.zero.title': 'Nenhuma conta está sendo acompanhada.',
  'balance.zero.title.source': 'No account is being followed.',
  'balance.zero.home': 'Suas campanhas estão prontas. Falta crédito para começar.',
  'balance.zero.home.source': 'Your campaigns are ready. Credit is what is missing to start.',
  'balance.zero.editor': 'Pode salvar. O acompanhamento começa quando entrarem créditos.',
  'balance.zero.editor.source': 'Go ahead and save. Following starts once credits arrive.',
  'balance.zero.accounts': 'Nenhuma destas contas está sendo acompanhada.',
  'balance.zero.accounts.source': 'None of these accounts is being followed.',
  'balance.zero.action': 'Pedir créditos',
  'balance.zero.action.source': 'Request credits',
  // The figure this sentence once interpolated renders in its own card now,
  // never parsed back out of prose.
  'balance.negative.title': 'O saldo ficou negativo. O acompanhamento está parado.',
  'balance.negative.title.source': 'The balance has gone negative. Following has stopped.',
  // `unknown` is a first-class acquisition state (state.ts): a failed
  // balance read must not render as a zero or negative balance, both of
  // which tell a paying tenant something specific and false about their
  // account.
  'balance.unknown': 'Não conseguimos mostrar o saldo agora.',
  'balance.unknown.source': 'We cannot show the balance right now.',
  'balance.unknown.action': 'Recarregue a página. O acompanhamento das contas continua.',
  'balance.unknown.action.source': 'Reload the page. Your accounts are still being followed.',

  // -- "Nothing fetched yet" (§18.3), named where an account or campaign is
  // first named, before any credit exists to follow anything about it.
  'nothing_fetched.account': 'Conta adicionada. O acompanhamento começa quando houver créditos.',
  'nothing_fetched.account.source': 'Account added. Following starts once there are credits.',
  'nothing_fetched.campaign': 'Campanha criada. Agora escolha as contas que ela vai acompanhar.',
  'nothing_fetched.campaign.source': 'Campaign created. Now choose the accounts it will follow.',

  // -- The missed schedule (§18.5): rendered as a `StatGrid` of `StatCard`s
  // (`MissedSchedule.tsx`), never as prose that interpolates its own figures.
  'missed.title': 'O que está parado',
  'missed.title.source': 'What is on hold',
  'missed.planned': 'Coletas previstas',
  'missed.planned.source': 'Collections planned',
  'missed.unfunded': 'Coletas perdidas',
  'missed.unfunded.source': 'Collections missed',
  'missed.since': 'Parado desde',
  'missed.since.source': 'On hold since',
  'missed.permanent': 'O que foi publicado nesse período não é recuperado depois.',
  'missed.permanent.source': 'What was posted in that time is not recovered later.',
  'missed.toFund': 'Créditos para cobrir tudo',
  'missed.toFund.source': 'Credits to cover it all',
  'missed.toFund.until': 'Cobre até {date}.',
  'missed.toFund.until.source': 'Covers through {date}.',
  'missed.toFund.unpriced': 'Sob consulta',
  'missed.toFund.unpriced.source': 'On request',

  // -- Partially funded (§18.5). A different state from a
  // zero balance: following IS happening, and a specific, nameable set of
  // accounts is outside it.
  'balance.partial.title': 'Nem todas as contas estão sendo acompanhadas.',
  'balance.partial.title.source': 'Not every account is being followed.',
  'balance.partial.watched': 'Contas observadas',
  'balance.partial.watched.source': 'Accounts watched',
  'balance.partial.shed': 'Fora da janela',
  'balance.partial.shed.source': 'Outside the window',
  'balance.partial.editor':
    'Pode salvar. Com o saldo de hoje, as contas do fim da lista ficam de fora até entrar ' +
    'mais crédito.',
  'balance.partial.editor.source':
    "Go ahead and save. At today's balance the accounts at the end of the list stay out " +
    'until more credit arrives.',
  'balance.partial.accounts': 'Estas contas estão fora do acompanhamento agora.',
  'balance.partial.accounts.source': 'These accounts are out of the follow list right now.',
  'balance.partial.action': 'Ver quais ficaram de fora',
  'balance.partial.action.source': 'See which ones are out',
  'shed.title': 'Contas que ficaram de fora',
  'shed.title.source': 'Accounts left out',
  'shed.why': 'Com o saldo de hoje não dá para acompanhar todas ao mesmo tempo.',
  'shed.why.source': "At today's balance not all of them can be followed at once.",
  'shed.lever.fewer': 'Acompanhar menos contas',
  'shed.lever.fewer.source': 'Follow fewer accounts',
  'shed.lever.credits': 'Pedir mais créditos',
  'shed.lever.credits.source': 'Request more credits',

  // -- Onboarding, where sign-up lands: an authenticated
  // user with no membership yet (§5.1). This copy promises no retention
  // period and must not acquire one -- no unused organisation here is
  // removed, expires, or goes dormant.
  'onboarding.title': 'Criar a sua organização',
  'onboarding.title.source': 'Create your organisation',
  'onboarding.stepLabel': 'Passo 1 de 2',
  'onboarding.stepLabel.source': 'Step 1 of 2',
  'onboarding.name.label': 'Nome da organização',
  'onboarding.name.label.source': 'Organisation name',
  'onboarding.name.hint': 'Visível para toda a equipe.',
  'onboarding.name.hint.source': 'Visible to the whole team.',
  'onboarding.submit': 'Continuar',
  'onboarding.submit.source': 'Continue',
  'onboarding.failed': 'Não deu para criar agora. Tente de novo.',
  'onboarding.failed.source': 'That did not work. Try again.',
  'onboarding.unavailable':
    'Ainda não dá para criar uma organização por aqui. Peça um convite para este e-mail a ' +
    'quem já usa a plataforma na sua equipe.',
  'onboarding.unavailable.source':
    'You cannot create an organisation here yet. Ask somebody on your team who already uses ' +
    'the platform to invite this email.',

  // -- Onboarding: capacity (step 2 of 2, CA-6) ----------------------------
  // This step creates the organisation (step 1 only names it); neither action
  // here blocks reaching the tenant home.
  'onboarding.capacity.stepLabel': 'Passo 2 de 2',
  'onboarding.capacity.stepLabel.source': 'Step 2 of 2',
  'onboarding.capacity.title': 'De quanto {organisation} vai precisar?',
  'onboarding.capacity.title.source': 'How much will {organisation} need?',
  'onboarding.capacity.lead':
    'A organização é criada agora. As vagas e os créditos são combinados com a gente e entram ' +
    'depois, sem travar o cadastro.',
  'onboarding.capacity.lead.source':
    'The organisation is created now. Slots and credits are worked out with us and come in ' +
    'afterwards, without blocking sign-up.',
  'onboarding.capacity.slots.label': 'Vagas de campanha',
  'onboarding.capacity.slots.label.source': 'Campaign slots',
  'onboarding.capacity.slots.decrease': 'Diminuir vagas de campanha',
  'onboarding.capacity.slots.decrease.source': 'Decrease campaign slots',
  'onboarding.capacity.slots.increase': 'Aumentar vagas de campanha',
  'onboarding.capacity.slots.increase.source': 'Increase campaign slots',
  'onboarding.capacity.credits.label': 'Créditos',
  'onboarding.capacity.credits.label.source': 'Credits',
  'onboarding.capacity.credits.option': '{amount} mil',
  'onboarding.capacity.credits.option.source': '{amount} thousand',
  'onboarding.capacity.credits.other': 'Outro',
  'onboarding.capacity.credits.other.source': 'Other',
  'onboarding.capacity.context': 'Solicitado durante a criação da organização.',
  'onboarding.capacity.context.source': 'Requested while creating the organisation.',
  'onboarding.capacity.failed': 'Não deu para enviar o pedido agora. Tente de novo.',
  'onboarding.capacity.failed.source': 'That request did not go through. Try again.',
  'onboarding.capacity.skip': 'Criar sem pedir agora',
  'onboarding.capacity.skip.source': 'Create without asking now',
  'onboarding.capacity.submit': 'Criar organização',
  'onboarding.capacity.submit.source': 'Create organisation',

  // -- Invitation acceptance ----------------------------------------------
  'accept.page_title': 'Convites',
  'accept.page_title.source': 'Invitations',
  'accept.invite.page_title': 'Você recebeu um convite',
  'accept.invite.page_title.source': "You've been invited",
  'accept.page_subtitle': 'Aqui estão os convites para você participar de organizações.',
  'accept.page_subtitle.source': 'Here are the invitations for you to join organisations.',
  'accept.switch_account_text': 'Você está usando a conta de {email}. Não é você?',
  'accept.switch_account_text.source': 'You are using the account {email}. Not you?',
  'accept.switch_account_action': 'Sair e acessar com sua conta',
  'accept.switch_account_action.source': 'Sign out and continue with your own account',
  'accept.verify_email.title': 'Confirme seu e-mail',
  'accept.verify_email.title.source': 'Confirm your email',
  'accept.verify_email.description':
    'Para acessar seus convites, precisamos ter certeza de que este e-mail é seu. Clique no ' +
    'link que acabamos de enviar para você.',
  'accept.verify_email.description.source':
    'To reach your invitations, we need to confirm this email is yours. Click the link we ' +
    'just sent you.',
  'accept.verify_email.resend_text': 'Não encontrou a mensagem?',
  'accept.verify_email.resend_text.source': "Didn't find the message?",
  'accept.verify_email.resend_action': 'Reenviar e-mail de confirmação',
  'accept.verify_email.resend_action.source': 'Resend confirmation email',
  'accept.invite.accept_action': 'Aceitar',
  'accept.invite.accept_action.source': 'Accept',
  'accept.invite.withdrawn_inline':
    'Este convite não está mais disponível. Fale com a pessoa que convidou você para pedir um novo.',
  'accept.invite.withdrawn_inline.source':
    'This invitation is no longer available. Ask whoever invited you for a new one.',
  'accept.empty_state':
    'Não há convites aguardando para este e-mail. Se você esperava encontrar um convite ' +
    'aqui, peça para seu colega enviá-lo novamente.',
  'accept.empty_state.source':
    'There are no invitations waiting for this email. If you expected to find one here, ask ' +
    'your colleague to send it again.',
  // Five separate refusal strings, one per status the server discloses to a
  // caller whose own verified address matches the invitation -- every other
  // cause (wrong address, unverified, no such invitation) is `.opaque`.
  'accept.refused.withdrawn': 'Este convite foi cancelado. Fale com seu colega para pedir um novo.',
  'accept.refused.withdrawn.source':
    'This invitation was cancelled. Ask your colleague for a new one.',
  'accept.refused.expired':
    'Este convite passou do prazo. Peça para a pessoa que convidou você enviar um novo.',
  'accept.refused.expired.source':
    'This invitation is past its deadline. Ask whoever invited you to send a new one.',
  'accept.refused.already_accepted': 'Você já faz parte desta organização.',
  'accept.refused.already_accepted.source': 'You are already part of this organisation.',
  'accept.refused.already_accepted_action': 'Acessar a organização',
  'accept.refused.already_accepted_action.source': 'Go to the organisation',
  'accept.refused.invalid': 'Este convite não pode ser usado. Peça para sua equipe enviar um novo.',
  'accept.refused.invalid.source':
    'This invitation cannot be used. Ask your team to send a new one.',
  'accept.refused.opaque':
    'Não foi possível abrir este convite. Tente pedir um novo para o seu colega.',
  'accept.refused.opaque.source':
    'This invitation could not be opened. Try asking your colleague for a new one.',

  // -- Request credits, the form. Distinct from the older
  // `creditRequests.create.*` keys above: those are not read by this
  // screen. `request.lead` renders unconditionally, in both the
  // create-form state and the already-open-request state -- it is what
  // tells a tenant where the answer will appear, regardless of which one
  // they are looking at.
  'request.title': 'Pedir créditos',
  'request.title.source': 'Request credits',
  'request.lead':
    'Conte o que você precisa acompanhar. A resposta aparece aqui mesmo, nesta página.',
  'request.lead.source':
    'Tell us what you need to follow. The answer appears right here, on this page.',
  'request.state.OPEN': 'Enviado',
  'request.state.OPEN.source': 'Sent',
  'request.state.IN_REVIEW': 'Em análise',
  'request.state.IN_REVIEW.source': 'Being looked at',
  'request.state.GRANTED': 'Atendido',
  'request.state.GRANTED.source': 'Granted',
  'request.state.DECLINED': 'Recusado',
  'request.state.DECLINED.source': 'Declined',
  'request.state.WITHDRAWN': 'Retirado',
  'request.state.WITHDRAWN.source': 'Withdrawn',
  'request.decided': 'Respondido em',
  'request.decided.source': 'Answered on',
  'request.decision_note': 'Resposta do time',
  'request.decision_note.source': "The team's reply",
  'request.accounts.label': 'Quantas contas você pretende acompanhar?',
  'request.accounts.label.source': 'How many accounts do you plan to track?',
  'request.credits.label': 'Quantos créditos você quer?',
  'request.credits.label.source': 'How many credits do you want?',
  'request.credits.hint': 'Não sabe? Deixe em branco e conte abaixo o que você precisa.',
  'request.credits.hint.source': 'Not sure? Leave it blank and say below what you need.',
  'request.context.label': 'O que você precisa acompanhar',
  'request.context.label.source': 'What you need to follow',
  'request.context.hint': 'Quais contas, em que período, e o que não pode passar batido.',
  'request.context.hint.source': 'Which accounts, over what period, and what must not be missed.',
  'request.context.required': 'Escreva o que você precisa acompanhar.',
  'request.context.required.source': 'Write what you need to follow.',
  'request.sent': 'Pedido enviado em {date}. Pode continuar montando suas campanhas enquanto isso.',
  'request.sent.source':
    'Request sent on {date}. You can carry on setting up your campaigns meanwhile.',
  'request.open_exists':
    'Você já tem um pedido enviado em {date}. Para mudar alguma coisa, retire este e envie ' +
    'outro.',
  'request.open_exists.source':
    'You already have a request sent on {date}. To change anything, withdraw it and send ' +
    'another.',
  'request.submit': 'Enviar pedido',
  'request.submit.source': 'Send request',
  'request.withdraw': 'Retirar pedido',
  'request.withdraw.source': 'Withdraw request',

  // -- Tenant home and campaign creation -------------------
  'home.heading': 'Painel',
  'home.heading.source': 'Dashboard',
  'home.newCampaign': 'Nova campanha',
  'home.newCampaign.source': 'New campaign',
  // The row-level mark (§5.4), distinct from `nothing_fetched.campaign`'s
  // fuller sentence: short enough to sit beside a name and a timezone rather
  // than replacing them.
  'campaigns.notCollected': 'Sem acompanhamento',
  'campaigns.notCollected.source': 'Not being followed',
  'newCampaign.submit': 'Criar campanha',
  'newCampaign.submit.source': 'Create campaign',

  // -- Invitations, the form and the list. Distinct from the older
  // `invitations.create.*`/`invitations.delivery.*` keys above: those are
  // not read by these two screens -- same situation as `request.*` above.
  'invite.delivery.before':
    'Você recebe um link para mandar para a pessoa. Nada é enviado automaticamente.',
  'invite.delivery.before.source':
    'You get a link to send to the person yourself. Nothing is sent automatically.',
  'invite.email.hint': 'Só quem entrar com este endereço consegue aceitar.',
  'invite.email.hint.source': 'Only somebody signing in with this address can accept.',
  'invite.duplicate':
    'Já existe um convite aberto para este endereço. Cancele-o antes de criar outro.',
  'invite.duplicate.source':
    'There is already an open invitation for this address. Cancel it before creating another.',
  'invite.issued.title': 'Convite criado. Copie o link agora.',
  'invite.issued.title.source': 'Invitation created. Copy the link now.',
  'invite.issued.once':
    'Este link aparece uma vez só. Se você sair desta tela sem copiar, cancele o convite e ' +
    'crie outro.',
  'invite.issued.once.source':
    'This link appears only once. If you leave without copying it, cancel the invitation ' +
    'and create another.',
  'invite.issued.send':
    'Agora mande o link para {email} do jeito que vocês já conversam. Ninguém foi avisado ainda.',
  'invite.issued.send.source':
    'Now send the link to {email} however the two of you already talk. Nobody has been ' +
    'notified yet.',
  'invite.copy': 'Copiar link',
  'invite.copy.source': 'Copy link',
  'invite.another': 'Convidar outra pessoa',
  'invite.another.source': 'Invite somebody else',
  // The page's own action, naming the person -- distinct from
  // `invite.submit`, the dialog's own title and submit label, which names
  // the record it creates.
  'invite.trigger': 'Convidar pessoa',
  'invite.trigger.source': 'Invite person',
  'invite.submit': 'Criar convite',
  'invite.submit.source': 'Create invitation',
  'invite.revoke': 'Cancelar',
  'invite.revoke.source': 'Cancel',

  // -- Navigation -----------------------------------------------------------
  'nav.home': 'Painel',
  'nav.home.source': 'Dashboard',
  'nav.team': 'Usuários',
  'nav.team.source': 'Users',
} as const;
